import { VantageNames } from '@/vantage/constants';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';

/**
 * Builds a stable identity string for an evidence table selection.
 *
 * Selection state is re-emitted asynchronously (e.g. after a server-side row model refresh or a
 * late `buildSimplifiedNodes` resolution) with enriched row content for the same logical
 * selection. Consumers that reset state when the selection changes must key off this identity
 * rather than deep-comparing the whole `SimplifiedNode[]` snapshot, otherwise a background data
 * refresh discards user input (CRAB-57561).
 *
 * @returns One `kind:capsuleIds` entry per selected node, sorted and joined by `;`, e.g.
 *   `'Capsule:datum-1;Condition:datum-2,datum-3'`. Capsules without a datum id fall back to
 *   `conditionId|start|end`, e.g. `'Capsule:cond-1|2026-01-01T00:00:00Z|2026-01-01T01:00:00Z'`.
 */
export const getSelectionIdentity = (selectedNodes: SimplifiedNode[]): string =>
  selectedNodes
    .map((node) => {
      const capsuleIds = (node.leafCapsules ?? [])
        .map(
          (capsule) =>
            capsule?.[VantageNames.Columns.DatumID] ||
            `${capsule?.[VantageNames.Columns.ConditionID]}|${capsule?.[VantageNames.Columns.Start]}|${
              capsule?.[VantageNames.Columns.End]
            }`,
        )
        .sort()
        .join(',');
      return `${node.kind}:${capsuleIds}`;
    })
    .sort()
    .join(';');
